/*!
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

export const NETWORK_ERROR_CODE = 502;
export const NETWORK_ERROR_MESSAGE =
  "Unable to connect to API, please try again.";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyError = any;

const isCancelledRequest = (err: AnyError): boolean =>
  err?.code === "ERR_CANCELED" ||
  err?.name === "CanceledError" ||
  err?.__CANCEL__ === true;

/** True when the browser reports no network connection. */
export const isBrowserOffline = (): boolean =>
  typeof navigator !== "undefined" && navigator.onLine === false;

/**
 * True when a request failed without any response from the server
 * (offline, DNS/CORS failure, server unreachable), excluding cancelled requests.
 */
export const isNetworkError = (err: AnyError): boolean =>
  !err?.response && !isCancelledRequest(err);

/** The "Unable to connect to API" error shown when a server can't be reached. */
export const getNetworkErrorMessage = () => ({
  type: "Network Error",
  code: NETWORK_ERROR_CODE,
  message: NETWORK_ERROR_MESSAGE,
  status: "",
});

/**
 * True for the synthetic error data attached by `toNetworkError` (or an error
 * carrying it).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isNetworkErrorPayload = (value: any): boolean =>
  value?.isNetworkError === true ||
  value?.response?.data?.isNetworkError === true;

/**
 * True when `message` is the connection error text ("Unable to connect to
 * API, please try again." or one of its `translations`). Error alerts filter
 * it out: connection problems are shown by NetworkRequestErrorAlert /
 * NetworkStatusAlert only.
 */
export const isNetworkErrorMessage = (
  message: unknown,
  ...translations: string[]
): boolean => {
  if (typeof message !== "string") return false;
  const text = message.trim();
  return (
    !!text &&
    [NETWORK_ERROR_MESSAGE, ...translations].some(
      (candidate) => candidate?.trim() === text
    )
  );
};

/**
 * Attaches a synthetic `response` to a network error so callers reading
 * `err.response.data` get a displayable error instead of crashing.
 */
export const toNetworkError = (err: AnyError): AnyError => {
  const response = {
    status: 0,
    statusText: "",
    headers: {},
    data: { ...getNetworkErrorMessage(), isNetworkError: true },
  };
  if (err && typeof err === "object") {
    err.response = response;
    return err;
  }
  return { message: NETWORK_ERROR_MESSAGE, isNetworkError: true, response };
};

// Set by NetworkStatusAlert from its feature flag (offline warning).
let networkAlertEnabled = false;

export const setNetworkAlertEnabled = (enabled: boolean): void => {
  networkAlertEnabled = enabled;
};

/** Which backend a request went to, e.g. "api", "clinician", "worklist". */
export type NetworkSource = string;

type Listener = () => void;

const notify = (listeners: Set<Listener>) =>
  listeners.forEach((listener) => listener());

const subscribeTo =
  (listeners: Set<Listener>) =>
  (listener: Listener): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

// Offline warning (NetworkStatusAlert): re-shown when a request fails offline.
const offlineFailureListeners = new Set<Listener>();
export const subscribeOfflineRequestFailure = subscribeTo(
  offlineFailureListeners
);

// "Unable to connect to API" alert (NetworkRequestErrorAlert): shown while a
// backend that failed without a response hasn't responded again.
const requestErrorListeners = new Set<Listener>();
let unreachableSources: NetworkSource[] = [];
let requestErrorDismissed = false;

export const subscribeRequestError = subscribeTo(requestErrorListeners);

export const isRequestErrorVisible = (): boolean =>
  unreachableSources.length > 0 && !requestErrorDismissed;

export const dismissRequestError = (): void => {
  requestErrorDismissed = true;
  notify(requestErrorListeners);
};

export const clearRequestErrors = (): void => {
  if (!unreachableSources.length) return;
  unreachableSources = [];
  notify(requestErrorListeners);
};

/** Called by the axios interceptors on every successful response. */
export const notifyRequestSucceeded = (source: NetworkSource = "api"): void => {
  if (!unreachableSources.includes(source)) return;
  unreachableSources = unreachableSources.filter((item) => item !== source);
  notify(requestErrorListeners);
};

/**
 * Shared handling for axios requests that failed without a response.
 * - cancelled request: rejected untouched;
 * - browser offline and the offline warning flag is on: re-shows the offline
 *   warning (NetworkStatusAlert);
 * - otherwise: shows "Unable to connect to API, please try again." at the top
 *   of the page content (NetworkRequestErrorAlert) until `source` responds
 *   again. Other error alerts filter that message out.
 * `onFailure` runs for every real network failure. Always rejects with a safe
 * error (`err.response.data` is defined).
 */
export const handleNetworkFailure = (
  err: AnyError,
  {
    source = "api",
    onFailure,
  }: { source?: NetworkSource; onFailure?: () => void } = {}
): Promise<never> => {
  if (!isNetworkError(err)) return Promise.reject(err);
  if (networkAlertEnabled && isBrowserOffline()) {
    notify(offlineFailureListeners);
  } else {
    if (!unreachableSources.includes(source)) {
      unreachableSources = [...unreachableSources, source];
    }
    requestErrorDismissed = false;
    notify(requestErrorListeners);
  }
  onFailure?.();
  return Promise.reject(toNetworkError(err));
};
